/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AclModel } from '../../../models';
import { Injectable } from '@angular/core';
import { LanguageService, NavigationService, UtilsService } from '../../../../../../core/services';
import { Layer4Template, Layer7Template } from '../../../templates';
import { NavigationExtras, Router } from '@angular/router';
import { WizardService, WizardTemplateService } from '../wizard';
import { WizardTitleEnum } from '../../../enums';

@Injectable()
export class AclService {
    /**
     * Available templates to select from.
     * @private
     */
    private availableTemplates: any[] = [Layer7Template, Layer4Template];

    constructor(
        private languageService: LanguageService,
        private navigationService: NavigationService,
        private router: Router,
        private utilsService: UtilsService,
        private wizardService: WizardService,
        private wizardTemplateService: WizardTemplateService
    ) {
    }

    /**
     * Add/edit acl.
     * @param frontends
     * @param backends
     * @param acls
     * @param redirectUrl
     * @param redirectText
     * @param backendId
     * @param frontendId
     * @param row
     * @param serviceType
     */
    public addEditAcl(
        frontends: any[],
        backends: any[],
        acls: any[],
        redirectUrl: string,
        redirectText: string,
        backendId?: string,
        frontendId?: string,
        row?: any,
        serviceType?: string
    ): void {
        const frontendObject: any[] = frontends.filter(item => item.id === frontendId);
        const frontendOptions: any = { inputsToShow: [], inputsToHide: ['all'], };
        const backendObject: any = backends.filter(backendServer => backendServer.id === backendId)[0];
        const aclObject: AclModel = this.getAclObject(acls, row, frontendId, backendId);
        const aclOptions: any = { inputsToShow: ['conditionType', 'redirectLocation', 'redirectType', 'serviceId'] };

        const templateType: string = serviceType || ((row && row.templateType) ? row.templateType : 'layer7');
        const serviceTemplate: any = this.wizardTemplateService.getClonedTemplate(this.availableTemplates, templateType);

        this.wizardService.controllerState = {
            [WizardTitleEnum.ACLS]: [
                this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.ACLS, aclObject, aclOptions)
            ]
        };

        if (!frontendId && backendId) {
            this.wizardService.controllerState[WizardTitleEnum.BACKENDS] = [
                this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backendObject)
            ];
        } else if (backendId) {
            if (backendId) {
                this.wizardService.controllerState[WizardTitleEnum.BACKENDS] = [backendObject];
            }
        } else if (frontendId) {
            this.wizardService.controllerState[WizardTitleEnum.FRONTENDS] = [
                this.wizardTemplateService.createServiceFromTemplate(
                    serviceTemplate, WizardTitleEnum.FRONTENDS, frontendObject[0], frontendOptions
                )
            ];
        }

        serviceTemplate.sections[WizardTitleEnum.ACLS][0].redirectLocation.hidden = false;

        delete serviceTemplate.sections[WizardTitleEnum.FRONTENDS];
        delete serviceTemplate.sections[WizardTitleEnum.REALSERVERS];
        delete serviceTemplate.sections[WizardTitleEnum.BACKENDS];

        const navigationExtras: NavigationExtras = {
            state: {
                wizardTitle: this.languageService.translate(frontendId ? 'acl_edit' : 'manage_acls_add'),
                cancelText: redirectText,
                templates: [serviceTemplate],
                parentUrl: redirectUrl,
                editMode: !!frontendId,
                duplicate: false,
                options: {
                    showBackButton: false,
                    showSaveButton: false,
                    showWizardMode: false,
                }
            }
        };

        const url: string = row ? (`/acl/${templateType}/edit/` + row.id) : `/acl/${templateType}/add`;

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url), navigationExtras).then().catch();
    }

    /**
     * Get ACL object from parameters.
     * @param acls
     * @param row
     * @param frontendId
     * @param backendId
     * @private
     */
    private getAclObject(acls: AclModel[], row: AclModel, frontendId: string, backendId: string): AclModel {
        if (row && frontendId) {
            const acl: AclModel = acls.find((acl: AclModel) => acl.id === row.id);
            acl.serviceId = frontendId;

            return acl;
        } else {
            return {
                id: 'temp-' + Date.now() + Math.random(),
                condition: null,
                conditionType: null,
                redirectLocation: backendId ? backendId : null,
                serviceId: frontendId ? frontendId : null,
                redirectType: backendId ? 'backend' : null
            };
        }
    }

    /**
     * Make match more user readable.
     * @param condition
     */
    public matchReadable(condition: string): string {
        switch (condition) {
            case 'eq':
                return 'Equals';
            case 'ne':
                return 'Not Equals';
            case 'lt':
                return 'Less Than';
            case 'le':
                return 'Less Than or Equal To';
            case 'gt':
                return 'Greater Than';
            case 'ge':
                return 'Greater Than or Equal To';
        }
    }

    /**
     * Make path type more user readable.
     * @param aclConditionType
     */
    public conditionTypeReadable(aclConditionType: string): string {
        switch (aclConditionType) {
            case 'pathBeg':
                return 'Path Begins';
            case 'pathEnd':
                return 'Path Ends';
            case 'hdrHost':
                return 'Header Host';
            case 'hdrBeg':
                return 'Header Begins (Host)';
            case 'sslSNI':
                return 'SSL Server Name Indication';
            case 'srcBlk':
                return 'IP Address';
            case 'nbs rv':
                return 'NB Srv';
            case 'default':
                return 'Default';
            default:
                return aclConditionType;
        }
    }

    /**
     * Make redirect type more user readable.
     * @param redirectType
     */
    public redirectTypeReadable(redirectType: string): string {
        switch (redirectType) {
            case 'drop':
                return 'Drop';
            case 'deny':
                return 'Deny';
            case 'urlLoc':
                return 'URL Location';
            case 'urlPre':
                return 'URL Prefix';
            case 'backend':
                return 'Backend';
            case 'server':
                return 'Server';
            case 'https':
                return 'HTTPS';
            case 'check':
                return 'Check';
            default:
                return redirectType;
        }
    }

    /**
     * Get backend name from backends or other location type if it has one.
     * @param acl
     * @param backends
     */
    public getBackendName(acl: AclModel, backends: any[]): string {
        if (this.utilsService.isServiceId(acl.redirectLocation)) {
            const match = backends.find(backend => backend.id === acl.redirectLocation);

            return match?.name ? match.name : acl.redirectLocation;
        } else {
            return acl.redirectLocation;
        }
    }
}
